import React, { useEffect, useState } from "react";
import {
  getADOAvailableFields,
  getAllAdoProjects,
  getAllSprintsFromADO,
  getEpicsUnderAdoProject,
  getFeaturesUnderEpic,
  getFilteredBugsFromADO,
  getTeamsFromADO,
  getUnassignedStoriesFromADO,
  getUserStoriesUnderFeatures,
  getUserStoriesUnderSprintFromADO,
  getTestCasesADO,
} from "../../../../services/webServices";
import JiraProjectsComponentPage from "../../../common-components/JiraProjectsComponentPage";
import Epics from "../../../common-components/Epics";
import TeamSelectionPage from "../../../common-components/TeamSelection";
import FeatureSelection from "../../../common-components/FeatureSelection";
import ImportUserStories from "../../../common-components/ImportUserStories";
import ImportTestCases from "../../../common-components/ImportTestCases";
import BugsFiltersPage from "../../../common-components/BugsFiltersPage";
import { useNavigate } from "react-router-dom";
import LoadingModal from "../../../common-components/LoadingModal";

const ADOSelectionPage = ({ issueType, setADOPageOpen }) => {
  const navigate = useNavigate();
  const [projectsList, setProjectsList] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [teamsList, setTeamsList] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [epicsList, setEpicsList] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [sprintsList, setSprintsList] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [unassignedStories, setUnassignedStories] = useState([]);
  const [selectedUnassignedUserStories, setSelectedUnassignedUserStories] =
    useState([]);
  const [featuresList, setFeaturesList] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [userStoriesList, setUserStoriesList] = useState([]);
  const [selectedUserStories, setSelectedUserStories] = useState([]);
  const [selectedBugFilters, setSelectedBugFilters] = useState({});
  const [testCasesList, setTestCasesList] = useState([]);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [importLoading, setImportLoading] = useState(null);
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      Promise.resolve().then(() => setImportLoading("Projects"));
 
      try {
        const getProjectsFromJiraAPI = await getAllAdoProjects();
        setProjectsList(getProjectsFromJiraAPI.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setImportLoading(null);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchTeams = async (projectName) => {
      Promise.resolve().then(() => setImportLoading("Teams"));

      try {
        const getTeams = await getTeamsFromADO(projectName);
        setTeamsList(getTeams.data);
      } catch (error) {
        console.error("Error fetching teams:", error);
      } finally {
        setImportLoading(null);
      }
    };

    if (selectedProject) {
      fetchTeams(selectedProject.name);
    } else {
      setSelectedTeam(null);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedTeam) {
      Promise.resolve().then(() => setImportLoading("Epics And Sprints"));

      const selectedProjectName = selectedProject.name;
      const selectedTeamName = selectedTeam.name;

      const getAllEpicsListFromSelectedProject = async () => {
        try {
          const getAllEpicsList = await getEpicsUnderAdoProject(
            selectedProjectName
          );
          setEpicsList(getAllEpicsList.data);
        } catch (error) {
          console.error("error", error);
        }
      };
      const getSprintsFromSelectedProjects = async () => {
        try {
          const getSprints = await getAllSprintsFromADO(selectedProjectName, selectedTeamName);
          setSprintsList(getSprints.data);
        } catch (error) {
          console.error("error", error);
        }
      };
      const getAllUnassignedUserStories = async () => {
        try {
          const getunassignedUserStories = await getUnassignedStoriesFromADO({
            projectName: selectedProjectName,
            issueType: "Story"
          });
          setUnassignedStories(getunassignedUserStories.data);
        } catch (error) {
          console.log("error", error);
        }
      };

      const fetchData = async () => {
        try {
          if (issueType === "User Stories") {
            await Promise.all([
              getAllEpicsListFromSelectedProject(),
              getSprintsFromSelectedProjects(),
              getAllUnassignedUserStories(),
            ]);
          }
          if (issueType === "Bugs") {
            await Promise.all([getSprintsFromSelectedProjects()]);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setImportLoading(null);
        }
      };

      fetchData();
    }
  }, [selectedTeam]);

  useEffect(() => {
    const getFeatures = async () => {
      try {
        Promise.resolve().then(() => setImportLoading("Features"));

        const getFeaturesList = await getFeaturesUnderEpic({
          projectName: selectedProject.name,
          epicKey: selectedEpic.id,
        });
        setFeaturesList(getFeaturesList.data);
      } catch (error) {
        console.error("error", error);
      } finally {
        setImportLoading(null);
      }
    };
    if (selectedEpic) {
      getFeatures();
    }
  }, [selectedEpic]);

  useEffect(() => {
    const getUserStories = async () => {
      try {
        Promise.resolve().then(() => setImportLoading("User Stories"));
        const getUserStoriesList = await getUserStoriesUnderFeatures({
          projectName: selectedProject.name,
          featureIds: selectedFeatures,
          issueType: "Story"
        });
        setUserStoriesList(getUserStoriesList.data);
      } catch (error) {
        console.error("error", error);
      } finally {
        setImportLoading(null);
      }
    };

    if (selectedFeatures?.length) {
      getUserStories();
    }
  }, [selectedFeatures]);

  useEffect(() => {
    const getUserStories = async () => {
      try {
        Promise.resolve().then(() => setImportLoading("User Stories"));

        const getUserStoriesList = await getUserStoriesUnderSprintFromADO({
          projectName: selectedProject.name,
          sprintPath: selectedSprint.path,
          issueType: "Story"
        });
        setUserStoriesList(getUserStoriesList.data);
      } catch (error) {
        console.error("error", error);
      } finally {
        setImportLoading(null);
      }
    };

    if (selectedSprint) {
      getUserStories();
    }
  }, [selectedSprint]);

  useEffect(() => {
    const getBugReportsByFilters = async () => {
      try {
        setLoader(true);
        const reqBody = {
          bugFilters: selectedBugFilters,
          projectName: selectedProject.name,
          issueType: "Bug",
        };
        const getBugsByFilter = await getFilteredBugsFromADO(reqBody);
        navigate("/dashboard/multi-bugreports/input", {
          state: { bugDetails: getBugsByFilter.data },
        });
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoader(false);
      }
    };

    if (Object.entries(selectedBugFilters).length > 0) {
      getBugReportsByFilters();
    }
  }, [selectedBugFilters]);

  // ✅ Fetch test cases for selected project + team
  useEffect(() => {
    const fetchTestCases = async () => {
      try {
        Promise.resolve().then(() => setImportLoading("Test Cases"));
        const res = await getTestCasesADO({
          projectName: selectedProject.name,
          issueType: "Test Cases",
        });
        setTestCasesList(res.data);
      } catch (error) {
        console.error("Error fetching test cases:", error);
      } finally {
        setImportLoading(null);
      }
    };

    if (issueType === "Test Cases" && selectedProject && selectedTeam) {
      fetchTestCases();
    }
  }, [selectedProject, selectedTeam]);

  const handleAvailableFields = async () => {
    const payload = {
      projectName: selectedProject.name,
      issueType,
    };

    try {
      const availableFields = await getADOAvailableFields(payload);
      return availableFields.data;
    } catch (error) {
      console.error("Error fetching available fields:", error);
    }
  };

  return (
    <div>
      {!selectedProject && (
        <JiraProjectsComponentPage
          taskType={"import"}
          issueType={issueType}
          projectsLoading={importLoading === "Projects"}
          setOpenProjectPage={setADOPageOpen}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          projectsList={projectsList}
        />
      )}

      {selectedProject && !selectedTeam && (
        <TeamSelectionPage
          issueType={issueType}
          teamsList={teamsList}
          setTeamsList={setTeamsList}
          teamsLoading={importLoading === "Teams"}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          setSelectedProject={setSelectedProject}
          setImportPageOpen={setADOPageOpen}
        />
      )}

      {issueType === "User Stories" && selectedProject && selectedTeam && (
        <>
          {!selectedEpic && !selectedSprint && !selectedFeatures.length && (
            <Epics
              epicsAndSprintsLoading={importLoading === "Epics And Sprints"}
              setSelectedParent={setSelectedTeam}
              setEpic={setSelectedEpic}
              setSprint={setSelectedSprint}
              epicsList={epicsList}
              setEpicsList={setEpicsList}
              sprintsList={sprintsList}
              setSprintsList={setSprintsList}
              unassignedStories={unassignedStories}
              setUnassignedStories={setUnassignedStories}
              selectedUnassignedUserStories={selectedUnassignedUserStories}
              setselectedUnassignedUserStories={
                setSelectedUnassignedUserStories
              }
              setImportOpen={setADOPageOpen}
              almConfig={{ 
                toolName: "Azure", 
                issueType,
              }}
            />
          )}

          {selectedEpic && (
            <FeatureSelection
              featuresLoading={importLoading === "Features"}
              featuresList={featuresList}
              setFeaturesList={setFeaturesList}
              selectedFeaturesList={selectedFeatures}
              setSelectedFeaturesList={setSelectedFeatures}
              setSelectedParent={setSelectedEpic}
              setImportPageOpen={setADOPageOpen}
            />
          )}

          {selectedFeatures.length > 0 && (
            <ImportUserStories
              almConfig={{ 
                toolName: "Azure", 
                issueType,
              }}
              userStoriesLoading={importLoading === "User Stories"}
              setSelectedParent={setSelectedFeatures}
              emptyParent={() => setSelectedFeatures([])}
              userStoriesList={userStoriesList}
              setUserStoriesList={setUserStoriesList}
              selectedUserStories={selectedUserStories}
              setSelectedUserStories={setSelectedUserStories}
              setImportPageOpen={setADOPageOpen}
            />
          )}

          {selectedSprint && (
            <ImportUserStories
              almConfig={{ 
                toolName: "Azure", 
                issueType,
              }}
              userStoriesLoading={importLoading === "User Stories"}
              setSelectedParent={setSelectedSprint}
              emptyParent={() => setSelectedSprint(null)}
              userStoriesList={userStoriesList}
              setUserStoriesList={setUserStoriesList}
              selectedUserStories={selectedUserStories}
              setSelectedUserStories={setSelectedUserStories}
              setImportPageOpen={setADOPageOpen}
            />
          )}
        </>
      )}

      {issueType === "Bugs" && selectedProject && selectedTeam && (
        <BugsFiltersPage
          filters={{
            Sprint: sprintsList.map((eachSprint) => ({
              name: eachSprint.name,
              value: eachSprint.path,
            })),
            State: [
              { name: "New", value: "New" },
              { name: "Active", value: "Active" },
              { name: "Resolved", value: "Resolved" },
              { name: "Closed", value: "Closed" },
            ],
            Reason: [
              { name: "New", value: "New" },
              { name: "Approved", value: "Approved" },
              { name: "Investigate", value: "Investigate" },
              { name: "As Designed", value: "As Designed" },
              { name: "Cannot Reproduce", value: "Cannot Reproduce" },
              { name: "Copied to Backlog", value: "Copied to Backlog" },
              { name: "Deferred", value: "Deferred" },
              { name: "Duplicate", value: "Duplicate" },
              { name: "Fixed", value: "Fixed" },
              { name: "Obsolete", value: "Obsolete" },
              { name: "Will not Fix", value: "Will not Fix" },
              { name: "Fixed and verified", value: "Fixed and verified" },
            ],
            Priority: [
              { name: "Critical", value: 1 },
              { name: "High", value: 2 },
              { name: "Medium", value: 3 },
              { name: "Low", value: 4 },
            ],
            Severity: [
              { name: "Critical", value: 1 },
              { name: "High", value: 2 },
              { name: "Medium", value: 3 },
              { name: "Low", value: 4 },
            ],
          }}
          setOpenFiltersPage={setADOPageOpen}
          setParent={setSelectedTeam}
          setFilterSubmit={setSelectedBugFilters}
        />
      )}

      {/* 5️⃣ Test Cases */}
      {issueType === "Test Cases" && selectedProject && selectedTeam && (
        <ImportTestCases
          almConfig={{ toolName: "Azure", issueType }}
          testCasesLoading={importLoading === "Test Cases"}
          testCasesList={testCasesList}
          setTestCasesList={setTestCasesList}
          selectedTestCases={selectedTestCases}
          setSelectedTestCases={setSelectedTestCases}
          setSelectedParent={setSelectedTeam}
          emptyParent={() => setSelectedTeam(null)}
          selectedProjectName={selectedProject.name}
          setImportPageOpen={setADOPageOpen}
        />
      )}

      {loader && <LoadingModal open={loader} />}
    </div>
  );
};

export default ADOSelectionPage;
